import { useEffect, useRef, useState } from "react";
import {
    AdjustmentsHorizontalIcon,
    BuildingStorefrontIcon,
    CheckIcon,
    ChevronDownIcon,
    CurrencyDollarIcon,
    TagIcon,
    TrashIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import { AddDrawerSelect } from "../productCatalog/AddDrawerSelect";

const SUPPLY_STATUS_OPTIONS = [
    { value: "ACTIVE", label: "Active" },
    { value: "PLANNED", label: "Planned" },
    { value: "SUNSET", label: "Sunset" },
    { value: "IN_ACTIVE", label: "Inactive" },
];

function cn(...parts) {
    return parts.filter(Boolean).join(" ");
}

function SelectionList({ items, selectedValue, onSelect, emptyText = "No options found" }) {
    return (
        <div className="max-h-[220px] overflow-y-auto mb-3 border border-gray-100 rounded-lg">
            {items.length > 0 ? (
                items.map((item) => {
                    const isSelected = String(selectedValue) === String(item.value);
                    return (
                        <button
                            key={String(item.value)}
                            type="button"
                            onClick={() => onSelect(item.value)}
                            className={cn(
                                "w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 border-b border-gray-50 last:border-0 transition-colors",

                            )}
                        >
                            <span className="text-xs text-gray-900">{item.label}</span>
                            {isSelected ? <CheckIcon className="h-3 w-3 text-gray-700" /> : null}
                        </button>
                    );
                })
            ) : (
                <p className="py-2 text-center text-xs text-gray-400">{emptyText}</p>
            )}
        </div>
    );
}

function ActionPopover({ open, onClose, children }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (e.target?.closest?.("[data-select-portal-panel='true']")) return;
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            ref={ref}
            className="absolute left-0 top-full z-50 mt-1.5 min-w-[280px] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        >
            {children}
        </div>
    );
}

/**
 * Dark bulk bar for Supplies tab — mirrors BomBulkEditBar.tsx layout.
 * Each action opens a popover; "Apply" requests parent to show ConfrimModal.
 */
export default function SuppliesBulkEditBar({
    selectedCount,
    supplyCategories = [],
    vendorOptions = [],
    vendorSelectLoading,
    onBulkApplyRequest,
    onClearSelection,
}) {
    const [active, setActive] = useState(null);
    const [categoryId, setCategoryId] = useState("");
    const [vendorId, setVendorId] = useState("");
    const [unitCost, setUnitCost] = useState("");
    const [status, setStatus] = useState("ACTIVE");
    const [openSelectKey, setOpenSelectKey] = useState(null);

    const closePopovers = () => setActive(null);

    const toggle = (key) => {
        setActive((prev) => (prev === key ? null : key));
    };

    useEffect(() => {
        if (!active) return undefined;
        setCategoryId("");
        setVendorId("");
        setUnitCost("");
        setStatus("ACTIVE");
        setOpenSelectKey(null);
        return undefined;
    }, [active]);

    if (!selectedCount) return null;

    const barBtn = (key, Icon, label) => (
        <button
            type="button"
            onClick={() => toggle(key)}
            className={cn(
                "flex h-7 items-center gap-1.5 px-2 text-xs font-semibold transition-colors text-white cursor-pointer",
                active === key ? "bg-white text-primary rounded" : ""
            )}
        >
            <Icon className={cn("h-4 w-4", active === key ? "text-primary" : "text-white")} />
            <span className={active === key ? "text-primary" : "text-white"}>{label}</span>
        </button>
    );

    const categoryOptions = [
        { value: "", label: "Select category..." },
        ...(supplyCategories || []).map((c) => ({
            value: String(c.id),
            label: c.name,
        })),
    ];

    const supplierOptions = [
        { value: "", label: "Select supplier..." },
        ...(vendorOptions || []).map((v) => ({
            value: String(v.value),
            label: v.label,
        })),
    ];

    return (
        <div>
            <div className="relative flex items-center gap-0 overflow-visible bg-[#111] px-4 py-2.5 text-white">
                <div className="mr-3 flex shrink-0 items-center gap-1.5 border-r border-white/10 pr-3">
                    <span className="text-xs font-semibold tabular-nums">{selectedCount} selected</span>
                </div>

                <div className="flex flex-1 flex-wrap items-center gap-0.5">
                    <div className="relative">
                        {barBtn("cost", CurrencyDollarIcon, "Update Cost")}
                        <ActionPopover open={active === "cost"} onClose={closePopovers}>
                            <div className="p-3">
                                <p className="mb-2 text-xs font-semibold tracking-wider text-gray-500">
                                    Set unit cost for {selectedCount} items
                                </p>
                                <label className="mb-1 block text-xs text-gray-500">Unit cost ($)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={unitCost}
                                    onChange={(e) => setUnitCost(e.target.value)}
                                    className="mb-3 h-8 w-full rounded border border-gray-200 px-2 text-right text-xs text-gray-900"
                                />
                                <div className="flex justify-end gap-2 border-t border-gray-100 pt-2">
                                    <button
                                        type="button"
                                        onClick={closePopovers}
                                        className="h-7 rounded border border-gray-200 px-3 text-xs text-gray-600 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={unitCost === "" || Number.isNaN(parseFloat(unitCost))}
                                        onClick={() => {
                                            const n = parseFloat(unitCost);
                                            if (Number.isNaN(n) || n < 0) return;
                                            onBulkApplyRequest({
                                                kind: "cost",
                                                unitCost: n,
                                            });
                                            closePopovers();
                                        }}
                                        className="flex h-7 items-center gap-1 rounded bg-gray-900 px-3 text-xs text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <CheckIcon className="h-3 w-3" />
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </ActionPopover>
                    </div>

                    <div className="relative">
                        {barBtn("supplier", BuildingStorefrontIcon, "Change Supplier")}
                        <ActionPopover open={active === "supplier"} onClose={closePopovers}>
                            <div className="p-3">
                                <p className="mb-2 text-xs text-gray-400">
                                    Reassign {selectedCount} item{selectedCount === 1 ? "" : "s"} to:
                                </p>
                                <div className="mb-3 max-w-[254px]">
                                    <AddDrawerSelect
                                        options={supplierOptions}
                                        value={vendorId}
                                        onChange={(value) => setVendorId(value || "")}
                                        noneLabel="Select supplier..."
                                        emptyLabel="Select supplier..."
                                        isLoading={vendorSelectLoading}
                                        selectClassname="w-full"
                                        buttonSelectedClassname="!py-1.5"
                                        renderInPortal
                                        stackKey="bulk-supplier"
                                        openStackKey={openSelectKey}
                                        onStackKeyChange={setOpenSelectKey}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 border-t border-gray-100 pt-2">
                                    <button
                                        type="button"
                                        onClick={closePopovers}
                                        className="h-7 rounded border border-gray-200 px-3 text-xs text-gray-600 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const v = (vendorOptions || []).find(
                                                (x) => String(x.value) === String(vendorId)
                                            );
                                            onBulkApplyRequest({
                                                kind: "supplier",
                                                vendorId: vendorId === "" ? null : String(vendorId),
                                                supplierLabel: v?.label || "None",
                                            });
                                            closePopovers();
                                        }}
                                        className="flex h-7 items-center gap-1 rounded bg-gray-900 px-3 text-xs text-white hover:bg-gray-800"
                                    >
                                        <CheckIcon className="h-3 w-3" />
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </ActionPopover>
                    </div>

                    <div className="relative">
                        {barBtn("status", AdjustmentsHorizontalIcon, "Set Status")}
                        <ActionPopover open={active === "status"} onClose={closePopovers}>
                            <div className="p-3">
                                <p className="mb-2 text-xs font-semibold tracking-wider text-gray-500">
                                    Set product status for {selectedCount} items
                                </p>
                                <SelectionList
                                    items={SUPPLY_STATUS_OPTIONS}
                                    onSelect={(value) => {
                                        const nextStatus = String(value);
                                        setStatus(nextStatus);
                                        const lab =
                                            SUPPLY_STATUS_OPTIONS.find((o) => o.value === nextStatus)?.label ||
                                            nextStatus;
                                        onBulkApplyRequest({
                                            kind: "status",
                                            status: nextStatus,
                                            statusLabel: lab,
                                        });
                                        closePopovers();
                                    }}
                                />
                            </div>
                        </ActionPopover>
                    </div>

                    <div className="relative">
                        {barBtn("category", TagIcon, "Recategorize")}
                        <ActionPopover open={active === "category"} onClose={closePopovers}>
                            <div className="p-3">
                                <p className="mb-2 text-xs text-gray-400">
                                    Move {selectedCount} item{selectedCount === 1 ? "" : "s"} to:
                                </p>
                                <div className="mb-3 max-w-[254px]">
                                    <AddDrawerSelect
                                        options={categoryOptions}
                                        value={categoryId}
                                        onChange={(value) => setCategoryId(value || "")}
                                        noneLabel="Select category..."
                                        emptyLabel="Select category..."
                                        selectClassname="w-full"
                                        buttonSelectedClassname="!py-1.5"
                                        renderInPortal
                                        stackKey="bulk-category"
                                        openStackKey={openSelectKey}
                                        onStackKeyChange={setOpenSelectKey}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 border-t border-gray-100 pt-2">
                                    <button
                                        type="button"
                                        onClick={closePopovers}
                                        className="h-7 rounded border border-gray-200 px-3 text-xs text-gray-600 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const cat = (supplyCategories || []).find(
                                                (c) => String(c.id) === String(categoryId)
                                            );
                                            onBulkApplyRequest({
                                                kind: "category",
                                                packagingCategoryId:
                                                    categoryId === "" ? null : Number(categoryId),
                                                categoryLabel: cat?.name || "None",
                                            });
                                            closePopovers();
                                        }}
                                        className="flex h-7 items-center gap-1 rounded bg-gray-900 px-3 text-xs text-white hover:bg-gray-800"
                                    >
                                        <CheckIcon className="h-3 w-3" />
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </ActionPopover>
                    </div>

                    <div className="mx-2 h-5 w-px bg-white/10" />
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            onBulkApplyRequest({ kind: "delete" });
                            closePopovers();
                        }}
                        className="flex shrink-0 items-center gap-1 border-l border-white/10 pl-3 text-xs font-semibold text-red-300 transition-colors hover:text-red-200"
                        title="Delete selection"
                    >
                        <TrashIcon className="h-3.5 w-3.5" />
                        <span>Delete</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onClearSelection();
                            closePopovers();
                        }}
                        className="flex shrink-0 items-center gap-1 text-xs text-white/50 transition-colors hover:text-white"
                        title="Clear selection"
                    >
                        <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>
        </div>
    );
}
